'use client';
import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { alpha, Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { setSelectMode, useAppDispatch } from '@/store';
import { EventResponse } from '@/types';
import { ResolvedTradeView } from '../markets/TradeModal';
import { SportsGameCard } from './SportsGameCard';

import { ROUTES } from '@/constants';

// Prototype "COMBO" accent (warm gold), independent of the MUI palette so it
// reads as the distinct combo entry point.
const COMBO_GOLD = '#E8B923';

interface TradeSidebarProps {
  selectedEvent: EventResponse | null;
  onClose?: () => void;
}

export const TradeSidebar = ({ selectedEvent, onClose }: TradeSidebarProps) => {
  const { t } = useT();
  const router = useRouter();
  const dispatch = useAppDispatch();

  // Enter combo-select mode and jump to the builder. Select mode also lets the
  // user keep tapping outcomes across the app to add legs.
  const handleOpenCombo = () => {
    dispatch(setSelectMode(true));
    router.push(ROUTES.COMBOS);
  };

  const winnerName = useMemo(() => {
    if (!selectedEvent) return t('trade.draw');
    const h = parseFloat(selectedEvent.homeTeam?.score || '0');
    const a = parseFloat(selectedEvent.awayTeam?.score || '0');
    if (h > a) return selectedEvent.homeTeam?.name || 'Home';
    if (a > h) return selectedEvent.awayTeam?.name || 'Away';
    return t('trade.draw');
  }, [selectedEvent, t]);

  if (!selectedEvent) {
    return null;
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 320 },
        flexShrink: 0,
        borderLeft: { xs: 'none', lg: '1px solid' },
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        height: { xs: 'auto', lg: 'calc(100vh - 100px)' },
        position: 'sticky',
        top: 20,
        overflowY: 'auto',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('trade.trade')}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={handleOpenCombo}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleOpenCombo()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            py: 1.5,
            borderRadius: '14px',
            cursor: 'pointer',
            color: COMBO_GOLD,
            border: `1px solid ${alpha(COMBO_GOLD, 0.4)}`,
            bgcolor: alpha(COMBO_GOLD, 0.08),
            transition: 'filter .15s, border-color .15s',
            '&:hover': { borderColor: COMBO_GOLD, filter: 'brightness(1.1)' },
          }}
        >
          <Box
            sx={{
              width: 13,
              height: 13,
              border: `1.6px solid ${COMBO_GOLD}`,
              borderRadius: '2px',
              transform: 'rotate(45deg)',
            }}
          />
          <Typography
            sx={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '0.16em', lineHeight: 1 }}
          >
            COMBO
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        <SportsGameCard
          event={selectedEvent}
          onTeamClick={() => {}} // No-op here maybe? Or trigger trade modal?
        />

        {selectedEvent.ended ||
        (selectedEvent.isLive === false &&
          (selectedEvent.homeTeam?.score || selectedEvent.awayTeam?.score)) ? (
          <ResolvedTradeView event={selectedEvent} winnerName={winnerName} />
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {t('trade.orderBook')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trade.selectOutcome')}
            </Typography>
            {/* Add dummy order book or trade controls here if needed */}
          </Box>
        )}
      </Box>
    </Box>
  );
};
